import React from "react";
import { Gem } from "lucide-react";
import Avatar from "@/components/Avatar";
import { TIERS } from "@/lib/tiers";

/**
 * "Requested By" supporter credit — live user reference.
 *
 *   💎 Requested by
 *   ◯ Cimengg
 *      GOD TIER SUPPORTER
 *
 * Accepts a hydrated user object `{id, name, avatar_url, role, badges}`
 * provided by the backend (`post.requested_by_user`). Renders nothing
 * when `user` is empty so missing/legacy posts stay invisible.
 *
 *  - `size="sm"` is for post cards (compact crimson chip).
 *  - `size="md"` is for the post detail page (premium pill below the title).
 */
export default function RequestedByBadge({ user, size = "md" }) {
  const u = user || null;
  if (!u || !(u.name || u.email)) return null;

  const tier = TIERS[u.role] || TIERS.member;
  const displayName = u.name || (u.email ? u.email.split("@")[0] : "Cultist");

  if (size === "sm") {
    // Compact one-row badge for post cards. Avatar 18px, single line of meta.
    return (
      <div
        data-testid="requested-by-card"
        className="
          mt-2 inline-flex items-center gap-2 px-2 py-1 rounded-md
          border border-rose-500/35
          bg-[linear-gradient(135deg,rgba(70,5,16,0.7)_0%,rgba(20,0,4,0.7)_100%)]
          shadow-[0_0_10px_-5px_rgba(225,29,72,0.45)]
        "
      >
        <Gem className="w-3 h-3 text-rose-300 shrink-0" strokeWidth={2.25} aria-hidden="true" />
        <Avatar user={u} size={18} />
        <div className="flex flex-col min-w-0 leading-tight">
          <span className="text-[11px] font-semibold text-rose-50 truncate max-w-[10rem]">
            {displayName}
          </span>
          <span className={`text-[9px] uppercase tracking-wider truncate max-w-[10rem] ${tier.color}`}>
            {tier.label}
          </span>
        </div>
      </div>
    );
  }

  // size === "md" — premium supporter-credit card on the detail page.
  return (
    <div
      data-testid="requested-by-detail"
      className="
        mt-3 inline-flex items-center gap-3 px-3.5 py-2 rounded-xl
        border border-rose-500/40
        bg-[linear-gradient(135deg,rgba(70,5,16,0.85)_0%,rgba(35,2,8,0.85)_50%,rgba(20,0,4,0.85)_100%)]
        shadow-[0_0_18px_-6px_rgba(225,29,72,0.55)]
      "
    >
      <div className="flex flex-col items-center gap-0.5 pr-2 border-r border-rose-500/25">
        <Gem
          className="w-4 h-4 text-rose-300 drop-shadow-[0_0_4px_rgba(244,63,94,0.6)]"
          strokeWidth={2.25}
          aria-hidden="true"
        />
        <span className="text-[10px] text-rose-200/85 leading-tight">Requested by</span>
      </div>
      <Avatar user={u} size={36} />
      <div className="flex flex-col min-w-0 leading-tight">
        <span className="font-display font-bold text-rose-50 text-base tracking-tight truncate max-w-[14rem]">
          {displayName}
        </span>
        <span className={`mt-0.5 text-[10px] font-bold uppercase tracking-wider truncate max-w-[14rem] ${tier.color}`}>
          {tier.label}
        </span>
      </div>
    </div>
  );
}
